<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BatchInvoiceEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    invoices: {
      type: Array,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const getItemFromInvoices = (...args) => {
    const [invoices, fields] = args;
    const fieldNames = _.map(fields, 'name');

    let result = {};

    console.log('getItemFromInvoices', fieldNames, fields, invoices);

    _.each(invoices, (invoice, i) => {
      if (i === 0) {
        result = _.pick(invoice, fieldNames);
      }

      for (const { name, defaultValue } of fields) {
        if (result[name] != invoice[name]) {
          result[name] = defaultValue;
          continue;
        }

        if (_.isUndefined(result[name])) {
          result[name] = defaultValue;
          continue;
        }

        if (result[name] != null) {
          result[name] = invoice[name];
        }
      }
    });

    result['$updates'] = _.reduce(fieldNames, (m, v) => _.set(m, v, false), {});

    console.log(result);

    return result;
  };
  const getPayload = (...args) => {
    const [invoices, item] = args;

    const updates = _.keys(_.pickBy(item.$updates, _.identity));

    if (!updates.length) {
      return;
    }

    const update = _.pick(item, updates);

    return {
      items: _.map(invoices, (invoice) => ({
        id: invoice.id,
        etag: invoice.etag,
        value: update,
      })),
    };
  };

  const isReadonly = ref(false);
  const editableFields = ref([
    {
      name: 'fulfillment_date',
    },
    {
      name: 'due_date',
    },
    {
      name: 'payment_method',
    },
    {
      name: 'currency',
    },
    {
      name: 'accounting_date',
    },
    {
      name: 'billing_period',
      defaultValue: {
        start: null,
        end: null,
      },
    },
    {
      name: 'notes',
    },
  ]);
  const labelAlign = ref('right');
  const formId = ref(null);
  const formName = ref('BatchInvoiceEdit');
  const item = ref(null);
  const currencies = ref(null);
  const paymentMethods = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    isReadonly: isReadonly.value,
    editableFields: editableFields.value,
    labelAlign: labelAlign.value,
    formId: formId.value,
    formName: formName.value,
    item: item.value,
    currencies: currencies.value,
    paymentMethods: paymentMethods.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = () => {
    return getItemFromInvoices(props.invoices, $data.value.editableFields);
  };
  const $$load_currencies = () => {
    return $app.value.repo.get('list_currency');
  };
  const $$load_paymentMethods = () => {
    return $app.value.repo
      .get('list_payment_method')
      .then((data) =>
        _.reduce(data, (m, v, k) =>
          _.set(m, v.id, $fn.value.pgettext('PaymentMethod', v.name))
        )
      );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'isReadonly' },
      { name: 'editableFields' },
      { name: 'labelAlign' },
      { name: 'formId' },
      { name: 'formName' },
      { name: 'item' },
      { name: 'currencies' },
      { name: 'paymentMethods' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      formId: $$load_formId,
      item: $$load_item,
      currencies: $$load_currencies,
      paymentMethods: $$load_paymentMethods,
    },
    ux: {},
    dataRefs: {
      isReadonly,
      editableFields,
      labelAlign,
      formId,
      formName,
      item,
      currencies,
      paymentMethods,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext(
        'BatchInvoiceEditor|Title',
        'Batch Edit Invoices'
      ),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.invoices,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoices');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_fwjc7wakh_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_e9pazefio_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_1040167445267876_request_6ifhy5m5l_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'bulk_update_invoice',
      data: getPayload(props.invoices, item.value),
    };
  };

  const act_7315092382398562_closeModal_fwjc7wakh =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_fwjc7wakh_getActionArgs,
    });
  const inst_evh_7315092382398562_close_wmjjfcd9f =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_fwjc7wakh,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_wmjjfcd9f],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_2sagrh4lr =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_owi92x0sf =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_2sagrh4lr,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_owi92x0sf],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_oq5n02e6o =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_kaofw65fk =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_oq5n02e6o,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_kaofw65fk],
    event: 'close',
  });
  const act_1040167445267876_closeModal_e9pazefio =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_e9pazefio_getActionArgs,
    });
  const inst_evh_1040167445267876_success_80psf1ffw =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_e9pazefio,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_request_6ifhy5m5l = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_6ifhy5m5l_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_80psf1ffw],
  });
  const inst_evh_1040167445267876_submit_vpwzcetkn =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_6ifhy5m5l,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_vpwzcetkn],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_bkx85b5ca =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_oqk8y85u1 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_bkx85b5ca,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_oqk8y85u1],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_wmjjfcd9f,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_owi92x0sf,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_kaofw65fk,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_80psf1ffw,
    inst_evh_1040167445267876_submit_vpwzcetkn,
    save: inst_evh_1040167445267876_submit_vpwzcetkn,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_oqk8y85u1,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_wmjjfcd9f = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_wmjjfcd9f.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_owi92x0sf = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_owi92x0sf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_kaofw65fk = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_kaofw65fk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_80psf1ffw = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_80psf1ffw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_vpwzcetkn = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_vpwzcetkn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_oqk8y85u1 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_oqk8y85u1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="BatchInvoiceEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        :name="formName"
        autofocus="first"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          name="fulfillment_date"
          :label="gettext('Fulfillment Date')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          required
          contentClass="input-group"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="fulfillment_date_enabled"
              v-model="item.$updates.fulfillment_date"
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormDatetime
            name="fulfillment_date"
            v-model="item.fulfillment_date"
            :disabled="!item.$updates.fulfillment_date"
            class="w-auto"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="due_date"
          :label="pgettext('Invoice', 'Due Date')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          required
          contentClass="input-group"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="due_date_enabled"
              v-model="item.$updates.due_date"
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormDatetime
            class="w-auto"
            name="due_date"
            v-model="item.due_date"
            :disabled="!item.$updates.due_date"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="payment_method"
          :label="pgettext('Invoice', 'Payment Method')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          required
          contentClass="input-group"
          class="col"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="payment_method_enabled"
              v-model="item.$updates.payment_method"
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormNativeSelect
            name="payment_method"
            v-model="item.payment_method"
            :options="paymentMethods"
            :disabled="!item.$updates.payment_method"
          >
          </ItpFormNativeSelect>
        </ItpFormGroup>
        <ItpFormGroup
          name="accounting_date"
          :label="pgettext('Invoice', 'Accounting Date')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="accounting_date_enabled"
              v-model="item.$updates.accounting_date"
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormDatetime
            name="accounting_date"
            v-model="item.accounting_date"
            :disabled="!item.$updates.accounting_date"
            class="w-auto"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="billing_period"
          :label="gettext('Billing Period')"
          contentClass="input-group"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          v-if="item.billing_period"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="billing_period_enabled"
              v-model="item.$updates.billing_period"
            >
            </ItpFormCheckbox>
          </div>
          <div class="row">
            <div class="col-4">
              <ItpFormDatetime
                name="billing_period_start"
                v-model="item.billing_period.start"
                :placeholder="pgettext('BillingPeriod', 'From')"
                :disabled="!item.$updates.billing_period"
              >
              </ItpFormDatetime>
            </div>
            <div class="col-4">
              <ItpFormDatetime
                name="billing_period_end"
                v-model="item.billing_period.end"
                :placeholder="pgettext('BillingPeriod', 'To')"
                :disabled="!item.$updates.billing_period"
              >
              </ItpFormDatetime>
            </div>
          </div>
        </ItpFormGroup>
        <ItpFormGroup
          name="currency"
          :label="pgettext('Invoice', 'Currency')"
          :label-cols-md="4"
          :content-cols-md="3"
          :labelAlign="labelAlign"
          contentClass="input-group"
          class="col"
        >
          <div class="input-group-text">
            <ItpFormCheckbox
              name="currency_enabled"
              v-model="item.$updates.currency"
            >
            </ItpFormCheckbox>
          </div>
          <ItpFormNativeSelect
            name="currency"
            v-model="item.currency"
            :options="currencies"
            valueField="code"
            textField="code"
            :disabled="!item.$updates.currency"
          >
          </ItpFormNativeSelect>
        </ItpFormGroup>
        <hr class="my-4" />
        <ItpFormGroup
          name="notes"
          :label="pgettext('Invoice', 'Notes')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          contentClass="input-group"
          class="col"
        >
          <div class="input-group-text">
            <ItpFormCheckbox name="notes_enabled" v-model="item.$updates.notes">
            </ItpFormCheckbox>
          </div>
          <ItpFormTextarea
            name="notes"
            v-model="item.notes"
            :disabled="!item.$updates.notes"
          >
          </ItpFormTextarea>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          :disabled="!forms[formName] || !forms[formName].submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
